import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { UserRound } from "lucide-react";
import api from "@api";
import DirectoryNavbar from "../Components/navbar/DirectoryNavbar.jsx";
import "./SportsCircle.css";
import { useAuth } from "../../../context/AuthContext";

const SportsCirclePage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [profile, setProfile] = useState(null);
  const [friends, setFriends] = useState([]);
  const [startingLineup, setStartingLineup] = useState([]);
  const [selectedLineupFriendId, setSelectedLineupFriendId] = useState(null);
  const [editingLineupEntry, setEditingLineupEntry] = useState(null);
  const [sports, setSports] = useState([]);
  const isOwnCircle = user?.id === profile?.user_id;

  useEffect(() => {
    const fetchSportsCircle = async () => {
      try {
        const [profileRes, friendsRes, lineupRes, sportsRes] = await Promise.all([
          api.get(`/api/user-profiles-v2/${id}`),
          api.get(`/api/friends/${id}`),
          api.get(`/api/starting-lineup/${id}`),
          api.get("/api/sports-catalog"),
        ]);

        setProfile(profileRes.data.profile);
        setFriends(friendsRes.data.friends || friendsRes.data || []);
        setStartingLineup(lineupRes.data.lineup || []);
        setSports(sportsRes.data.sports || sportsRes.data || []);
      } catch (error) {
        console.error("Failed to load Sports Circle:", error);
      }
    };

    fetchSportsCircle();
  }, [id]);

  const handleAddToStartingLineup = async (
    friendProfileId,
    sportId
  ) => {
    try {
      const res = await api.post("/api/starting-lineup", {
        friend_profile_id: friendProfileId,
        sport_id: sportId,
      });

      setStartingLineup((prev) => [
        ...prev,
        res.data.lineupEntry,
      ]);
    } catch (error) {
      console.error("Failed to add to Starting Lineup:", error);
    }
  };

  const handleRemoveFromStartingLineup = async (friendProfileId) => {
    const confirmed = window.confirm(
      "Remove this person from your Starting Lineup?"
    );

    if (!confirmed) return;

    try {
      await api.delete(`/api/starting-lineup/${friendProfileId}`);

      setStartingLineup((prev) =>
        prev.filter(
          (entry) => entry.friend_profile_id !== friendProfileId
        )
      );
    } catch (error) {
      console.error("Failed to remove from Starting Lineup:", error);
    }
  };

  const handleRemoveFriend = async (friend) => {
    const confirmed = window.confirm("Remove this friend?");

    if (!confirmed) return;

    try {
      const isInLineup = startingLineup.some(
        (entry) => entry.friend_profile_id === friend.profile.id
      );

      if (isInLineup) {
        await api.delete(
          `/api/starting-lineup/${friend.profile.id}`
        );

        setStartingLineup((prev) =>
          prev.filter(
            (entry) =>
              entry.friend_profile_id !== friend.profile.id
          )
        );
      }

      await api.delete(
        `/api/friends/${friend.connection_id}`
      );

      setFriends((prev) =>
        prev.filter(
          (item) =>
            item.connection_id !== friend.connection_id
        )
      );
    } catch (error) {
      console.error("Failed to remove friend:", error);
    }
  };


  const handleUpdateStartingLineup = async (
    friendProfileId,
    sportId,
    currentQuote = ""
  ) => {
    const quote = window.prompt(
      "Add a short Starting Lineup quote (80 characters max):",
      currentQuote
    );

    if (quote === null) return;

    const cleanedQuote = quote.trim();

    if (cleanedQuote.length > 80) {
      window.alert("Quote must be 80 characters or less.");
      return;
    }

    try {
      const res = await api.patch(
        `/api/starting-lineup/${friendProfileId}`,
        {
          quote: cleanedQuote,
          sport_id: sportId,
        }
      );

      const selectedSport =
        sports.find((sport) => sport.id === Number(sportId)) || null;

      setStartingLineup((prev) =>
        prev.map((entry) =>
          entry.friend_profile_id === friendProfileId
            ? {
              ...entry,
              quote: res.data.lineupEntry.quote,
              sport_id: res.data.lineupEntry.sport_id,
              sport: selectedSport,
            }
            : entry
        )
      );

      setEditingLineupEntry(null);
      setSelectedLineupFriendId(null);
    } catch (error) {
      console.error("Failed to update Starting Lineup:", error);
    }
  };

  const getFriendForLineupEntry = (entry) => {
    return friends.find(
      (friend) =>
        friend.profile.id === entry.friend_profile_id
    );
  };



  const getSportIcon = (slug) => {
    const icons = {
      basketball: "🏀",
      tennis: "🎾",
      football: "🏈",
      baseball: "⚾",
      soccer: "⚽",
      volleyball: "🏐",
      golf: "⛳",
      bowling: "🎳",
      swimming: "🏊",
      running: "🏃",
      "jiu-jitsu": "🥋",
      taekwondo: "🥋",
      "water-polo": "🤽",
      "roller-derby": "🛼",
    };

    return icons[slug] || "⭐";
  };

  if (!profile) {
    return <div>Loading...</div>;
  }

  return (
    <div className="sports-circle-page">
      <DirectoryNavbar />

      <main className="sports-circle-container">
        <div className="sports-circle-page-header">
          <h1>
            {profile.first_name} {profile.last_name}'s Sports Circle
          </h1>

          <p>
            {friends.length}{" "}
            {friends.length === 1 ? "friend" : "friends"}
          </p>
        </div>

        <div
          className={`sports-circle-layout ${isOwnCircle ? "with-lineup" : "single-column"
            }`}
        >
          {/* LEFT — SPORTS CIRCLE */}
          <section className="sports-circle-main">


            <div className="sports-circle-list">
              {friends.length === 0 && (
                <div className="sports-circle-empty">
                  No friends yet.
                </div>
              )}

              {friends.map((friend) => {
                const person = friend.profile;

                const isInStartingLineup =
                  startingLineup.some(
                    (entry) =>
                      entry.friend_profile_id === person.id
                  );

                return (
                  <div
                    key={person.id}
                    className="sports-circle-person-row"
                    onClick={() =>
                      navigate(`/profile/${person.id}`)
                    }
                  >
                    <div className="sports-circle-person-info">
                      {person.profile_image ? (
                        <img
                          src={person.profile_image}
                          alt={`${person.first_name || ""} ${person.last_name || ""
                            }`}
                        />
                      ) : (
                        <div className="sports-circle-person-fallback">
                          <UserRound size={22} />
                        </div>
                      )}

                      <span>
                        {`${person.first_name || ""} ${person.last_name || ""
                          }`.trim() || person.display_name}
                      </span>
                    </div>

                    {isOwnCircle && (
                      <div className="sports-circle-actions">
                        {!isInStartingLineup ? (
                          <button
                            type="button"
                            className="lineup-add-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedLineupFriendId(person.id);
                            }}
                          >
                            + Starting Lineup
                          </button>
                        ) : (
                          <span className="lineup-added-label">
                            ✓ Starting Lineup
                          </span>
                        )}

                        <button
                          type="button"
                          className="sports-circle-remove-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRemoveFriend(friend);
                          }}
                        >
                          Remove Friend
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </section>

          {/* RIGHT — STARTING LINEUP */}
          {isOwnCircle && (
            <aside className="starting-lineup-panel">
              <div className="starting-lineup-header">
                <div>
                  <h2>My Starting Lineup</h2>
                  <p>
                    {startingLineup.length} / 5 selected
                  </p>
                </div>
              </div>

              <div className="starting-lineup-slots">
                {[1, 2, 3, 4, 5].map((position) => {
                  const entry = startingLineup.find(
                    (item) => item.position === position
                  );

                  const friend = entry
                    ? getFriendForLineupEntry(entry)
                    : null;

                  if (!entry || !friend) {
                    return (
                      <div
                        key={position}
                        className="starting-lineup-slot empty"
                      >
                        <span className="starting-lineup-position">
                          {position}
                        </span>

                        <div className="starting-lineup-empty-text">
                          Empty spot
                        </div>
                      </div>
                    );
                  }

                  const person = friend.profile;

                  return (
                    <div
                      key={position}
                      className="starting-lineup-slot"
                    >
                      <span className="starting-lineup-position">
                        {position}
                      </span>

                      {person.profile_image ? (
                        <img
                          src={person.profile_image}
                          alt={`${person.first_name || ""} ${person.last_name || ""
                            }`}
                        />
                      ) : (
                        <div className="starting-lineup-fallback">
                          <UserRound size={18} />
                        </div>
                      )}

                      <div className="starting-lineup-person">
                        <strong>
                          {`${person.first_name || ""} ${person.last_name || ""
                            }`.trim() || person.display_name}
                        </strong>
                        {entry.quote ? (
                          <button
                            type="button"
                            className="starting-lineup-quote"
                            onClick={() => {
                              setEditingLineupEntry(entry);
                              setSelectedLineupFriendId(person.id);
                            }}
                          >
                            “{entry.quote}”
                          </button>
                        ) : (
                          <button
                            type="button"
                            className="starting-lineup-add-quote"
                              onClick={() => {
                                setEditingLineupEntry(entry);
                                setSelectedLineupFriendId(person.id);
                              }}
                          >
                            + Add quote
                          </button>
                        )}
                      </div>

                      <button
                        type="button"
                        className="starting-lineup-remove-btn"
                        onClick={() =>
                          handleRemoveFromStartingLineup(
                            person.id
                          )
                        }
                      >
                        ×
                      </button>
                    </div>
                  );
                })}
              </div>
            </aside>
          )}
          
        </div>
        {selectedLineupFriendId && (
          <div
            className="lineup-sport-picker-overlay"
            onClick={() => setSelectedLineupFriendId(null)}
          >
            <div
              className="lineup-sport-picker"
              onClick={(e) => e.stopPropagation()}
            >
              <h3>What sport are they in your lineup for?</h3>

              <div className="lineup-sport-picker-options">
                {sports.map((sport) => (
                  <button
                    key={sport.id}
                    type="button"
                    onClick={() => {
                      if (editingLineupEntry) {
                        handleUpdateStartingLineup(
                          selectedLineupFriendId,
                          sport.id,
                          editingLineupEntry.quote || ""
                        );
                      } else {
                        handleAddToStartingLineup(
                          selectedLineupFriendId,
                          sport.id
                        );
                      }

                      setSelectedLineupFriendId(null);
                    }}
                  >
                    <span>{getSportIcon(sport.slug)}</span>
                    <span>{sport.name}</span>
                  </button>
                ))}
              </div>

              <button
                type="button"
                className="lineup-no-sport-btn"
                onClick={() => {
                  if (editingLineupEntry) {
                    handleUpdateStartingLineup(
                      selectedLineupFriendId,
                      null,
                      editingLineupEntry.quote || ""
                    );
                  } else {
                    handleAddToStartingLineup(
                      selectedLineupFriendId,
                      null
                    );

                    setSelectedLineupFriendId(null);
                  }
                }}
              >
                No sport
              </button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default SportsCirclePage;